import React, {
    useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Paper from '@mui/material/Paper';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
// import HelpOutlineIcon from '@mui/icons-material/HelpOutlineOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { ensureArray } from 'BMapsUtil/js_utils';
import Dialog from './Dialog';
import { DialogCancelButton, DialogSubmitButton } from './DialogActionButtons';
import DialogHeader from './DialogHeader';

/**
 * @typedef {import('./Dialog').DialogProps} DialogProps
 */

let openConfirm = null;

export function registerConfirmOpen(fn) {
    openConfirm = fn;
}

/**
 * Per-call options for the dialog helpers. Replaces the old bare
 * `resolveFunction` argument; visual overrides take precedence over the
 * defaults in {@link TYPE_META} for that single dialog.
 *
 * @typedef {Object} DialogParams
 * @property {(result: boolean) => void} [resolve]
 *   Called with the user's response (true = confirmed/acknowledged, false = cancelled).
 * @property {React.ElementType | null} [Icon]
 *   Override the dialog type's default icon. Pass `null` to show no icon.
 * @property {string} [color]
 *   Override the accent color as an MUI palette token string, e.g. 'error.main'.
 */

/**
 * @param {'confirm'|'alert'|'warning'} type
 * @param {string} consoleLabel - fallback console prefix when no provider is mounted
 * @param {string} title
 * @param {message[] | message | string[] | string | React.ReactElement[] | React.ReactElement} body
 * @param {DialogParams} [params]
 */
function openDialog(type, consoleLabel, title, body, params={}) {
    const { resolve, ...overrides } = params;
    if (!openConfirm) {
        console.warn(consoleLabel, title, body);
        if (resolve) resolve(true);
        return null;
    }
    const formattedContent = formatDialogBody(body);
    return openConfirm({ title, body: formattedContent, ...overrides }, type, resolve);
}

/**
 * @param {string} title
 * @param {message[] | message | string[] | string | React.ReactElement[] | React.ReactElement} body
 * @param {DialogParams} [params] - resolve callback and optional icon/color overrides
 * @returns
 */
export function dialogConfirm(title, body, params={}) {
    return openDialog('confirm', 'User Confirmation:', title, body, params);
}

/**
 * @param {string} title
 * @param {message[] | message | string[] | string | React.ReactElement[] | React.ReactElement} body
 * @param {DialogParams} [params] - resolve callback and optional icon/color overrides
 * @returns
 */
export function dialogAlert(title, body, params={}) {
    return openDialog('alert', 'User Alert:', title, body, params);
}

/**
 * Icon and accent color for each dialog type.
 * Colors use MUI SX theme-token strings so they automatically adapt to theme overrides.
 * Adding a new entry here automatically applies the alert visual treatment
 * (accent stripe on the Paper, accent title bar, grip glyph, grab cursor).
 * - alert: informational (blue info icon)
 * - confirm: question / decision (orange warning icon) (matches jConfirm)
 * - warning: warning or error (orange warning icon)
 */
const TYPE_META = {
    alert: { Icon: InfoOutlinedIcon, color: 'info.main' },
    // confirm: { Icon: HelpOutlineIcon, color: 'info.main' },
    confirm: { Icon: WarningAmberIcon, color: 'warning.main' },
    warning: { Icon: WarningAmberIcon, color: 'warning.main' },
};

// Resolve an MUI palette token string like 'info.main' to a real color value.
function resolvePaletteColor(theme, token) {
    if (!token || typeof token !== 'string') return undefined;
    const [paletteKey, shadeKey='main'] = token.split('.');
    return theme.palette?.[paletteKey]?.[shadeKey];
}

/**
 * A Paper component that can be dragged via the element marked with [data-drag-handle].
 * Dragging is achieved using CSS transform so the dialog stays centered by default and
 * can be moved freely by the user.
 *
 * To make other MUI Dialogs draggable, pass this as the PaperComponent prop:
 *   <Dialog PaperComponent={DraggablePaper} ...>
 * and ensure the drag handle element carries the data-drag-handle attribute (DialogHeader
 * already adds this attribute so any dialog using DialogHeader gets drag-from-title-bar
 * for free once PaperComponent={DraggablePaper} is set).
 *
 * Considerations for other modal dialogs:
 *  - Large / fullscreen dialogs may feel awkward when draggable; consider gating by screen size.
 *  - Dialogs that scroll internal content (maxHeight) work fine since pointer capture is released
 *    on pointerup regardless.
 *  - Position resets automatically when the dialog is unmounted (closed), because component
 *    state is discarded. Dialogs that use keepMounted will retain their last position.
 *  - The transform used for dragging composes with any existing Paper style.transform, so
 *    MUI transitions and custom styles are not clobbered.
 */
// Interactive elements inside the drag handle should not initiate a drag.
const DRAG_IGNORE_SELECTORS = 'button, a, input, textarea, select, [role="button"], [role="link"]';

export const DraggablePaper = React.forwardRef((props, ref) => {
    const [pos, setPos] = useState({ x: 0, y: 0 });
    const posRef = useRef({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    // Store cleanup function so it can be called on unmount or before a new drag starts.
    const cleanupRef = useRef(null);
    const theme = useTheme();

    useEffect(() => () => cleanupRef.current?.(), []);

    const onPointerDown = useCallback((e) => {
        // Only respond to the primary pointer button (left mouse / first touch).
        if (e.button !== 0) return;
        // Only initiate drag when the pointer lands on a designated drag handle.
        if (!e.target.closest('[data-drag-handle]')) return;
        // Ignore clicks on interactive descendants (close button, links, inputs, etc.).
        if (e.target.closest(DRAG_IGNORE_SELECTORS)) return;

        // Clean up any in-progress drag before starting a new one (re-entrancy / multi-touch).
        cleanupRef.current?.();

        e.preventDefault();

        const startX = e.clientX - posRef.current.x;
        const startY = e.clientY - posRef.current.y;

        setIsDragging(true);

        const onMove = (ev) => {
            const newPos = { x: ev.clientX - startX, y: ev.clientY - startY };
            posRef.current = newPos;
            setPos(newPos);
        };

        const onUp = () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
            window.removeEventListener('pointercancel', onUp);
            cleanupRef.current = null;
            setIsDragging(false);
        };

        cleanupRef.current = onUp;
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
        window.addEventListener('pointercancel', onUp);
    }, []);

    const { style: paperStyle, typeMeta, ...paperProps } = props;
    // Compose the drag translation with any transform already on the Paper element
    // so MUI's own animation/layout transforms are not clobbered.
    const baseTransform = paperStyle?.transform ?? '';
    const dragTransform = `translate(${pos.x}px, ${pos.y}px)`;
    const composedTransform = baseTransform ? `${baseTransform} ${dragTransform}` : dragTransform;
    // When the dialog is one of the alert family (typeMeta present), apply an accent
    // top stripe so it reads as a system message rather than a feature dialog.
    const accentColor = typeMeta ? resolvePaletteColor(theme, typeMeta.color) : undefined;
    return (
        <Paper
            {...paperProps}
            ref={ref}
            onPointerDown={onPointerDown}
            // Surface drag state so descendants (DialogHeader) can swap cursor to 'grabbing'.
            data-dragging={isDragging ? 'true' : undefined}
            style={{
                ...paperStyle,
                transform: composedTransform,
                // Disable text selection only while actively
                // dragging to avoid disrupting copy/paste.
                userSelect: isDragging ? 'none' : undefined,
                ...(accentColor && {
                    borderTop: `6px solid ${accentColor}`,
                    overflow: 'hidden',
                }),
            }}
        />
    );
});

export default function DialogProvider({ children }) {
    const [queue, setQueue] = useState([]);
    const current = queue[0];

    const open = useCallback((options={}, type, resolveFunction) => {
        setQueue((q) => q.concat([{
            body: options.body,
            title: options.title,
            type,
            resolve: resolveFunction,
            // Per-call visual overrides. Only carry keys the caller actually
            // supplied so `Icon: null` (hide icon) stays distinct from "use default".
            overrides: {
                ...('Icon' in options ? { Icon: options.Icon } : null),
                ...('color' in options ? { color: options.color } : null),
            },
        }]));
    }, []);
    useEffect(() => registerConfirmOpen(open), [open]);

    const resolveConfirmation = useCallback((result) => {
        setQueue((q) => {
            if (!q.length) return q;
            const [head, ...rest] = q;
            if (head.resolve) head.resolve(result);
            return rest;
        });
    }, []);

    const body = current?.body;
    // Merge the current dialog's per-call overrides over its type defaults.
    // Memoized so the reference is stable for TypedPaper useMemo below
    // (current/current.overrides identity only changes when the queue head does).
    const typeMeta = useMemo(() => {
        const baseMeta = TYPE_META[current?.type];
        if (!baseMeta) return undefined;
        const overrides = current?.overrides ?? {};
        return {
            Icon: 'Icon' in overrides ? overrides.Icon : baseMeta.Icon,
            color: overrides.color ?? baseMeta.color,
        };
    }, [current?.type, current?.overrides]);
    const { Icon: TypeIcon, color: iconColor } = typeMeta ?? {};

    // Inject the current dialog's typeMeta into DraggablePaper so the Paper can
    // apply the alert accent treatment without coupling itself to TYPE_META.
    const TypedPaper = useMemo(
        () => React.forwardRef((paperProps, paperRef) => (
            <DraggablePaper {...paperProps} ref={paperRef} typeMeta={typeMeta} />
        )),
        [typeMeta],
    );

    return (
        <>
            {children}
            <Dialog
                open={Boolean(current)}
                onEscape={() => resolveConfirmation(false)}
                onClose={(_, reason) => { if (reason !== 'backdropClick') resolveConfirmation(false); }}
                closeOnEscape
                transitionDuration={0}
                slotProps={{ backdrop: { transitionDuration: 0 } }}
                maxWidth="md"
                PaperComponent={TypedPaper}
                onKeyUp={(e) => {
                    if (e.key === 'Escape') {
                        e.stopPropagation();
                        resolveConfirmation(false);
                    }
                }}
            >

                {!!current?.title
                 && (
                 <DialogHeader
                     handleClose={() => resolveConfirmation(false)}
                     title={current.title}
                     draggable
                     typeMeta={typeMeta}
                 />
                 )}
                <DialogContent sx={{ paddingBottom: '1rem' }}>
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'flex-start',
                        gap: '1rem',
                        marginTop: '1rem',
                    }}
                    >
                        {TypeIcon && (
                            <TypeIcon
                                aria-hidden
                                sx={{
                                    fontSize: '2.5rem',
                                    color: iconColor,
                                    flexShrink: 0,
                                    mt: '0.25rem',
                                }}
                            />
                        )}
                        <Box sx={{ overflow: 'auto', flex: 1 }}>
                            {body}
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    {current?.type === 'alert' && <AlertButtons resolveConfirmation={resolveConfirmation} />}
                    {current?.type === 'warning' && <AlertButtons resolveConfirmation={resolveConfirmation} />}
                    {current?.type === 'confirm' && <ConfirmButtons resolveConfirmation={resolveConfirmation} />}
                </DialogActions>
            </Dialog>
        </>
    );
}

function ConfirmButtons({ resolveConfirmation, confirmText='OK', cancelText='Cancel' }) {
    return (
        <Box sx={{
            display: 'flex', flexDirection: 'row', gap: '1rem', width: '100%',
        }}
        >
            <DialogSubmitButton
                onClick={() => resolveConfirmation(true)}
                sx={{ flex: '1 1 0', minWidth: '0', maxWidth: '100%' }}
            >
                {confirmText}
            </DialogSubmitButton>
            <DialogCancelButton
                onClick={() => resolveConfirmation(false)}
                sx={{ flex: '1 1 0', minWidth: '0', maxWidth: '100%' }}
            >
                {cancelText}
            </DialogCancelButton>
        </Box>
    );
}

function AlertButtons({ resolveConfirmation, confirmText='OK' }) {
    return (
        <Box sx={{
            display: 'flex', flexDirection: 'row', justifyContent: 'center', width: '100%',
        }}
        >
            <DialogSubmitButton
                onClick={() => resolveConfirmation(true)}
                sx={{ flexGrow: 1 }}
            >
                {confirmText}
            </DialogSubmitButton>
        </Box>
    );
}

/**
 * @typedef {{ header: string, fileNames: string[], footer: string}} message
 *
 * @param {message[] | message | string[] | string | React.ReactElement[] | React.ReactElement} msgs
 * @returns {React.ReactElement}
 */
export function formatDialogBody(msgs) {
    let messages = msgs;
    if (!Array.isArray(messages)) messages = [messages];

    const body = messages.map((message, msgI) => {
        if (React.isValidElement(message)) {
            return message;
        }
        const lines = ensureArray(message).flatMap((line) => {
            const myLines = [];
            if (typeof line === 'string') {
                for (const l of line.split('\n')) {
                    if (!l) {
                        myLines.push(<br />);
                    } else if (l.startsWith('* ')) {
                        myLines.push(<li style={{ marginLeft: '2em' }}>{l.slice(2)}</li>);
                    } else {
                        // TODO dangerouslySetInnerHTML may no longer be necessary
                        // (originally used for About, which is now JSX)
                        myLines.push(<span dangerouslySetInnerHTML={{ __html: l }} />);
                    }
                }
            } else {
                myLines.push(line);
            }
            return myLines;
        });
        return (
            <Box key={`dialog-msg-${msgI.toString()}`}>
                <Typography component="div" sx={{ margin: '.5rem 0' }}>
                    {lines.map((line, lineI) => (
                        <div key={`dialog-line-${lineI.toString()}`}>{line}</div>
                    ))}
                </Typography>
            </Box>
        );
    });
    return (
        <Box sx={{ lineHeight: '1.5', display: 'flex', flexDirection: 'column' }}>
            {body}
        </Box>
    );
}
